import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiService';
import Button from '../ui/Button';
import HomeFeedCard from './HomeFeedCard';
import type { HomeFeedResponse, HomeFeedSection, HomeFeedSummary, HomeQuickAction } from './homeTypes';
import { clientTimeZone, formatHomeFeedDateLabel } from '../../utils/homeFeedDate';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'actions', label: 'Actions' },
  { id: 'events', label: 'Events' },
  { id: 'passport', label: 'Passport' },
  { id: 'builds', label: 'Builds' },
  { id: 'clubs', label: 'Clubs' },
  { id: 'shops', label: 'Shops' },
] as const;

interface HomeFeedPanelProps {
  onErrorChange?: (error: boolean) => void;
  onFeedMeta?: (meta: {
    sections: HomeFeedSection[];
    quick: HomeQuickAction[];
    summary: HomeFeedSummary | null;
  }) => void;
}

export const HomeFeedPanel: React.FC<HomeFeedPanelProps> = ({ onErrorChange, onFeedMeta }) => {
  const navigate = useNavigate();
  const [feed, setFeed] = useState<HomeFeedResponse | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(
    async (opts?: { cursor?: string; append?: boolean }) => {
      if (opts?.append) setLoadingMore(true);
      else setLoading(true);
      setError(false);
      onErrorChange?.(false);
      try {
        const data = await apiService.getHomeFeed({
          types: filter === 'all' ? undefined : filter,
          cursor: opts?.cursor,
          limit: 30,
          timezone: clientTimeZone() || undefined,
        });
        setFeed((prev) => {
          if (opts?.append && prev) {
            const mergedSections = [...prev.sections];
            for (const section of data.sections) {
              const existing = mergedSections.find((s) => s.id === section.id);
              if (existing) {
                existing.items = [...existing.items, ...section.items];
              } else {
                mergedSections.push(section);
              }
            }
            return { ...data, sections: mergedSections, summary: prev.summary, quick_actions: prev.quick_actions };
          }
          return data;
        });
        void apiService.trackEvent('home_feed_loaded', {
          metadata: { filter, item_count: data.item_count ?? 0 },
        });
      } catch {
        setError(true);
        onErrorChange?.(true);
        if (!opts?.append) setFeed(null);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filter, onErrorChange]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const summary = feed?.summary;
  const sections = feed?.sections ?? [];
  const empty = !loading && sections.length === 0;

  useEffect(() => {
    if (!feed) return;
    onFeedMeta?.({
      sections: feed.sections ?? [],
      quick: feed.quick_actions ?? [],
      summary: feed.summary ?? null,
    });
  }, [feed, onFeedMeta]);

  const dateLabel = useMemo(
    () => formatHomeFeedDateLabel(feed?.generated_at ?? new Date()),
    [feed?.generated_at]
  );

  return (
    <div id="home-feed" className="min-w-0">
      <header className="mb-5 sm:mb-6">
        <p className="spec-label text-zinc-500">{dateLabel}</p>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white tracking-tight mt-1">
          {summary?.greeting || 'Welcome back'}
        </h2>
        <p className="mt-2 text-sm sm:text-[15px] text-zinc-400 max-w-2xl leading-relaxed">
          {summary?.headline || 'Your Bilt:Spec activity will show up here.'}
        </p>
        {summary && summary.counts.action_required > 0 ? (
          <p className="mt-2 text-xs text-orange-300/90">
            {summary.counts.action_required} need
            {summary.counts.action_required === 1 ? 's' : ''} attention today
          </p>
        ) : null}
      </header>

        <div
          className="sticky top-0 z-10 -mx-1 px-1 py-2 mb-4 bg-[#0a0a0a]/90 backdrop-blur-sm border-b border-white/[0.04]"
          role="toolbar"
          aria-label="Feed filters"
        >
          <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
            {FILTERS.map((f) => {
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => {
                    setFilter(f.id);
                    void apiService.trackEvent('home_feed_filter_used', { metadata: { filter: f.id } });
                  }}
                  className={`shrink-0 min-h-[40px] px-3 rounded-md text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 ${
                    active
                      ? 'bg-white/10 text-white'
                      : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04]'
                  }`}
                  aria-pressed={active}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {error ? (
          <div className="rounded-lg border border-white/[0.08] bg-[#0e0e0e] px-4 py-4 mb-4" role="alert">
            <p className="text-sm text-zinc-400">The feed could not load. Your garage is still available.</p>
            <Button type="button" variant="secondary" className="mt-3 min-h-[44px]" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        ) : null}

        {loading ? (
          <div className="space-y-3" aria-busy="true" aria-label="Loading feed">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 rounded-lg bg-white/[0.03] animate-pulse" />
            ))}
          </div>
        ) : null}

        {empty ? (
          <div
            className="rounded-lg border border-dashed border-white/10 px-5 py-10 text-center"
            data-testid="home-feed-empty"
          >
            <p className="font-display text-lg text-white">You&apos;re caught up</p>
            <p className="mt-2 text-sm text-zinc-400 max-w-md mx-auto">
              Registered events, club and event updates, Passport scans, and build progress show up here as they
              happen. Until then, Discover still has builds and events worth exploring.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Button type="button" className="min-h-[44px]" onClick={() => navigate('/events')}>
                Find events
              </Button>
              <Button type="button" variant="secondary" className="min-h-[44px]" onClick={() => navigate('/discover')}>
                Explore builds
              </Button>
            </div>
          </div>
        ) : null}

        <div className="space-y-10">
          {sections.map((section) => (
            <section
              key={section.id}
              id={`feed-section-${section.id}`}
              aria-labelledby={`feed-section-heading-${section.id}`}
            >
              <h3
                id={`feed-section-heading-${section.id}`}
                className="mb-3 sticky top-12 z-[5] bg-[#0a0a0a]/90 py-1.5 backdrop-blur-sm flex items-baseline gap-2"
              >
                <span className="font-display text-sm font-semibold tracking-wide text-zinc-300 uppercase">
                  {section.title}
                </span>
                <span className="h-px flex-1 bg-gradient-to-r from-white/10 to-transparent" aria-hidden />
                <span className="text-[11px] tabular-nums text-zinc-600">
                  {section.items.length}
                </span>
              </h3>
              <ul className="list-none p-0 m-0 grid grid-cols-1 gap-3 md:gap-4">
                {section.items.map((item) => (
                  <li key={item.item_key} className="min-w-0">
                    <HomeFeedCard item={item} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        {feed?.next_cursor ? (
          <div className="mt-6 flex justify-center">
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px]"
              disabled={loadingMore}
              onClick={() => {
                void apiService.trackEvent('home_feed_older_loaded', { metadata: { cursor: feed.next_cursor } });
                void load({ cursor: feed.next_cursor!, append: true });
              }}
            >
              {loadingMore ? 'Loading…' : 'Load earlier activity'}
            </Button>
          </div>
        ) : null}
    </div>
  );
};

export default HomeFeedPanel;
